const chapter = {
  id: 'hydrocarbons',
  slug: 'hydrocarbons',
  subject: 'chemistry',
  subjectLabel: 'Chemistry',
  exam: 'JEE / NEET',
  title: {
    english: 'Hydrocarbons',
        hindi: 'हाइड्रोकार्बन',
  },
  estReadMinutes: 60,
  difficulty: {
    english: 'Medium',
        hindi: 'मध्यम',
  },
  weightage: {
    english: '2-3 questions in JEE / NEET',
        hindi: 'जेईई/एनईईटी में 2-3 प्रश्न',
  },
  syllabusTags: ['Organic Chemistry', 'Class-11', 'JEE Main', 'NEET'],
  sections: [
    {
      id: 'classification',
      title: {
        english: 'Classification',
        hindi: 'वर्गीकरण',
      },
      blocks: [
        {
          type: 'list',
          items: [
            {
              english: '**Saturated:** alkanes ($C_nH_{2n+2}$, only C-C single bonds).',
        hindi: '**संतृप्त:** अल्केन्स ($C_nH_{2n+2}$, केवल C-C एकल बांड)।',
            },
            {
              english: '**Unsaturated:** alkenes ($C_nH_{2n}$, C=C); alkynes ($C_nH_{2n-2}$, C≡C).',
        hindi: '**असंतृप्त:** एल्केन्स ($C_nH_{2n}$, C=C); एल्काइन्स ($C_nH_{2n-2}$, C≡C)।',
            },
            {
              english: '**Cyclic alicyclic:** cycloalkanes / cycloalkenes.',
        hindi: '**चक्रीय एलिसाइक्लिक:** साइक्लोअल्केन्स / साइक्लोअल्केन्स।',
            },
            {
              english: '**Aromatic (arenes):** benzene & derivatives — planar, conjugated, $(4n+2)\\pi$ electrons (Hückel).',
        hindi: '**एरोमैटिक (एरेनेस):** बेंजीन और डेरिवेटिव - समतल, संयुग्मित, $(4n+2)\\pi$ इलेक्ट्रॉन (ह्यूकेल)।',
            },
          ],
        },
      ],
    },
    {
      id: 'alkanes',
      title: {
        english: 'Alkanes — Preparation & Reactions',
        hindi: 'एल्केन — तैयारी और अभिक्रियाएँ',
      },
      blocks: [
        {
          type: 'list',
          items: [
            {
              english: '**Preparations:**',
        hindi: '**तैयारियाँ:**',
            },
            {
              english: '• **Wurtz**: $2 R-X + 2 Na \\xrightarrow{\\text{dry ether}} R-R + 2 NaX$ (gives symmetrical alkanes).',
        hindi: '• **वुर्ट्ज़**: $2 R-X + 2 Na \\xrightarrow{\\text{सूखा ईथर}} R-R + 2 NaX$ (सममित अल्केन्स देता है)।',
            },
            {
              english: '• **Kolbe electrolysis**: electrolysis of conc. soln of Na/K salt of carboxylic acid → alkane at anode.',
        hindi: '• **कोल्बे इलेक्ट्रोलिसिस**: सांद्र का इलेक्ट्रोलिसिस। कार्बोक्जिलिक एसिड के Na/K नमक का सोलन → एनोड पर एल्केन।',
            },
            {
              english: '• **Decarboxylation**: Na salt of acid + soda lime ($NaOH + CaO$) → $RH + Na_2CO_3$.',
        hindi: '• **डीकार्बाक्सिलेशन**: एसिड + सोडा लाइम का Na नमक ($NaOH + CaO$) → $RH + Na_2CO_3$।',
            },
            {
              english: '• **Hydrogenation**: alkene/alkyne + $H_2$ / Ni or Pt or Pd → alkane.',
        hindi: '• **हाइड्रोजनीकरण**: एल्केन/एल्केनी + $H_2$ / Ni या Pt या Pd → एल्केन।',
            },
            {
              english: '• **Reduction of alkyl halides**: $R-X + Zn / HCl$ or $LiAlH_4$.',
        hindi: '• **अल्काइल हैलाइड्स की कमी**: $R-X + Zn / HCl$ या $LiAlH_4$। **प्रतिक्रियाएँ:**',
            },
            {
              english: '**Reactions:**',
        hindi: '**प्रतिक्रियाएँ:**',
            },
            {
              english: '• **Halogenation** (free-radical, sunlight/UV): $CH_4 + Cl_2 \\to CH_3Cl + HCl$ (then further). Reactivity: $F > Cl > Br > I$. Selectivity: $3°H > 2°H > 1°H$.',
        hindi: '• **हैलोजनीकरण** (मुक्त-रेडिकल, सूरज की रोशनी/यूवी): $CH_4 + Cl_2 \\to CH_3Cl + HCl$ (फिर आगे)। प्रतिक्रियाशीलता: $F > Cl > Br > I$. चयनात्मकता: $3°H > 2°H > 1°H$.',
            },
            {
              english: '• **Combustion**: $C_nH_{2n+2} + (3n+1)/2\\,O_2 \\to n CO_2 + (n+1) H_2O$.',
        hindi: '• **दहन**: $C_nH_{2n+2} + (3n+1)/2\\,O_2 \\to n CO_2 + (n+1) H_2O$.',
            },
            {
              english: '• **Pyrolysis (cracking)**: high T → smaller alkanes/alkenes + $H_2$.',
        hindi: '• **पाइरोलिसिस (क्रैकिंग)**: उच्च टी → छोटा alkanes/alkenes + $H_2$।',
            },
            {
              english: '• **Aromatisation (reforming)**: $n$-hexane → benzene, $Cr_2O_3-Al_2O_3$, 773 K.',
        hindi: '• **एरोमेटाइजेशन (सुधार)**: $n$-हेक्सेन → बेंजीन, $Cr_2O_3-Al_2O_3$, 773 K.',
            },
          ],
        },
      ],
    },
    {
      id: 'alkenes',
      title: {
        english: 'Alkenes — Preparation & Reactions',
        hindi: 'एल्कीन — तैयारी और अभिक्रियाएँ',
      },
      blocks: [
        {
          type: 'list',
          items: [
            {
              english: '**Preparations:**',
        hindi: '**तैयारियाँ:**',
            },
            {
              english: '• Dehydrohalogenation of alkyl halide with alc. KOH → **Saytzeff**: more substituted alkene preferred.',
        hindi: '• एल्क के साथ एल्काइल हैलाइड का डीहाइड्रोहैलोजनीकरण। KOH → **सैटज़ेफ़**: अधिक प्रतिस्थापित एल्कीन को प्राथमिकता दी जाती है।',
            },
            {
              english: '• Dehydration of alcohol with conc. $H_2SO_4$ at 443 K (1° alcohol harder; 3° easiest). Reactivity: $3° > 2° > 1°$.',
        hindi: '• सांद्रण के साथ अल्कोहल का निर्जलीकरण। 443 K पर $H_2SO_4$ (1° अल्कोहल कठिन; 3° सबसे आसान)। प्रतिक्रियाशीलता: $3° > 2° > 1°$.',
            },
            {
              english: '• Dehalogenation of vicinal dihalide with $Zn$ in alcohol.',
        hindi: '• अल्कोहल में $Zn$ के साथ विसिनल डाइहैलाइड का डीहेलोजनेशन। **प्रतिक्रियाएं (इलेक्ट्रोफिलिक जोड़):**',
            },
            {
              english: '**Reactions (electrophilic addition):**',
        hindi: '**प्रतिक्रियाएं (इलेक्ट्रोफिलिक जोड़):**',
            },
            {
              english: '• $H_2$ / Ni → alkane.',
        hindi: '• $H_2$ / Ni → एल्केन।',
            },
            {
              english: '• $X_2$ → vicinal dihalide. (Reactivity: $Cl_2 > Br_2 > I_2$.)',
        hindi: '• $X_2$ → विसिनल डाइहैलाइड। (प्रतिक्रियाशीलता: $Cl_2 > Br_2 > I_2$.)',
            },
            {
              english: '• $HX$ → **Markovnikov**: H adds to C with more H (more stable carbocation).',
        hindi: '• $HX$ → **मार्कोव्निकोव**: H अधिक H (अधिक स्थिर कार्बोकेशन) के साथ C को जोड़ता है।',
            },
            {
              english: '• $HBr + $ peroxide → **anti-Markovnikov (Kharasch)** — only with HBr (free radical).',
        hindi: '• $HBr + $ पेरोक्साइड → **एंटी-मार्कोवनिकोव (खाराश)** - केवल HBr (मुक्त रेडिकल) के साथ।',
            },
            {
              english: '• $H_2O / H_2SO_4$ → alcohol (Markovnikov).',
        hindi: '• $H_2O / H_2SO_4$ → अल्कोहल (मार्कोव्निकोव)।',
            },
            {
              english: '• $H_2SO_4$ → alkyl hydrogen sulphate → hydrolysis → alcohol.',
        hindi: '• $H_2SO_4$ → एल्काइल हाइड्रोजन सल्फेट → हाइड्रोलिसिस → अल्कोहल।',
            },
            {
              english: '• Cold dilute alkaline $KMnO_4$ (**Baeyer\'s test**) → vicinal diol (decolourises pink).',
        hindi: '• ठंडा पतला क्षारीय $KMnO_4$ (**बेयर का परीक्षण**) → विसिनल डायोल (गुलाबी रंग बदलता है)।',
            },
            {
              english: '• Hot conc. $KMnO_4$ → cleavage to ketones / acids / $CO_2$.',
        hindi: '• हॉट कॉन्स। $KMnO_4$ → कीटोन्स / एसिड / $CO_2$ में दरार।',
            },
            {
              english: '• Ozonolysis ($O_3$ then $Zn/H_2O$) → reductive cleavage to carbonyls; identifies alkene structure.',
        hindi: '• ओजोनोलिसिस ($O_3$ फिर $Zn/H_2O$) → कार्बोनिल्स में रिडक्टिव क्लीवेज; एल्केन संरचना की पहचान करता है।',
            },
            {
              english: '• Polymerisation: $n CH_2=CH_2 \\to (-CH_2-CH_2-)_n$.',
        hindi: '• पॉलिमराइजेशन: $n CH_2=CH_2 \\to (-CH_2-CH_2-)_n$.',
            },
          ],
        },
      ],
    },
    {
      id: 'alkynes',
      title: {
        english: 'Alkynes — Preparation & Reactions',
        hindi: 'एल्काइन — तैयारी और अभिक्रियाएँ',
      },
      blocks: [
        {
          type: 'list',
          items: [
            {
              english: '**Preparations:**',
        hindi: '**तैयारियाँ:**',
            },
            {
              english: '• $CaC_2 + 2 H_2O \\to C_2H_2 + Ca(OH)_2$ (acetylene from calcium carbide).',
        hindi: '• $CaC_2 + 2 H_2O \\to C_2H_2 + Ca(OH)_2$ (कैल्शियम कार्बाइड से प्राप्त एसिटिलीन)।',
            },
            {
              english: '• Dehydrohalogenation of vicinal/geminal dihalide with alc. KOH (excess).',
        hindi: '• एल्क के साथ vicinal/geminal डाइहैलाइड का डीहाइड्रोहैलोजनीकरण। KOH (अतिरिक्त)।',
            },
            {
              english: '**Reactions:**',
        hindi: '**प्रतिक्रियाएँ:**',
            },
            {
              english: '• Acidic H (terminal alkyne, $sp$-C with 50% s) → reacts with $Na/Na NH_2$ → sodium acetylide; with $AgNO_3/NH_4OH$ → white ppt; with $Cu_2Cl_2/NH_4OH$ → red ppt (test for terminal alkynes).',
        hindi: '• अम्लीय H (टर्मिनल एल्काइन, $sp$-C 50% s के साथ) → $Na/Na NH_2$ → सोडियम एसिटाइलाइड के साथ प्रतिक्रिया करता है; $AgNO_3/NH_4OH$ → सफेद पीपीटी के साथ; $Cu_2Cl_2/NH_4OH$ → लाल पीपीटी (टर्मिनल एल्काइन्स के लिए परीक्षण) के साथ।',
            },
            {
              english: '• Addition of $H_2$: with **Lindlar catalyst** (Pd-CaCO₃ poisoned with Pb) → cis-alkene. With $Na/liq.\\,NH_3$ → trans-alkene.',
        hindi: '• $H_2$ का जोड़: **लिंडलर उत्प्रेरक** के साथ (Pd-CaCO₃ Pb के साथ जहर) → सिस-अल्कीन। $Na/liq.\\,NH_3$ → ट्रांस-अल्कीन के साथ।',
            },
            {
              english: '• $HX, X_2$: two successive additions, Markovnikov.',
        hindi: '• $HX, X_2$: लगातार दो अतिरिक्त, मार्कोवनिकोव।',
            },
            {
              english: '• $H_2O / H_2SO_4 / HgSO_4$: forms enol → tautomerises → ketone (acetylene → acetaldehyde; higher → ketone).',
        hindi: '• $H_2O / H_2SO_4 / HgSO_4$: फॉर्म एनोल → टॉटोमेराइजेस → कीटोन (एसिटिलीन → एसीटैल्डिहाइड; उच्चतर → कीटोन)।',
            },
            {
              english: '• Polymerisation: 3 acetylene → benzene (red-hot Cu tube, 873 K).',
        hindi: '• पॉलिमराइजेशन: 3 एसिटिलीन → बेंजीन (लाल-गर्म Cu ट्यूब, 873 K)।',
            },
          ],
        },
      ],
    },
    {
      id: 'aromatic',
      title: {
        english: 'Aromatic Hydrocarbons — Benzene',
        hindi: 'सुगन्धित हाइड्रोकार्बन — बेन्ज़ीन',
      },
      blocks: [
        {
          type: 'list',
          items: [
            {
              english: '**Aromaticity (Hückel rule):** planar, fully conjugated, cyclic, $(4n+2)\\pi$ electrons. Benzene: $n=1$, 6π e⁻.',
        hindi: '**सुगंधितता (हुकेल नियम):** तलीय, पूर्णतः संयुग्मित, चक्रीय, $(4n+2)\\pi$ इलेक्ट्रॉन। बेंजीन: $n=1$, 6π e⁻.',
            },
            {
              english: '**Reactions — Electrophilic Aromatic Substitution (EAS):**',
        hindi: '**प्रतिक्रियाएँ - इलेक्ट्रोफिलिक सुगंधित प्रतिस्थापन (ईएएस):**',
            },
            {
              english: '• **Halogenation**: $C_6H_6 + X_2 / FeX_3 \\to C_6H_5X + HX$.',
        hindi: '• **हैलोजनीकरण**: $C_6H_6 + X_2 / FeX_3 \\to C_6H_5X + HX$.',
            },
            {
              english: '• **Nitration**: $HNO_3 / H_2SO_4 \\to C_6H_5NO_2$ (electrophile $NO_2^+$).',
        hindi: '• **नाइट्रेशन**: $HNO_3 / H_2SO_4 \\to C_6H_5NO_2$ (इलेक्ट्रोफाइल $NO_2^+$)।',
            },
            {
              english: '• **Sulphonation**: conc. $H_2SO_4$ or oleum → benzenesulphonic acid (electrophile $SO_3$).',
        hindi: '• **सल्फ़ोनेशन**: संक्षिप्त। $H_2SO_4$ या ओलियम → बेंजीनसल्फोनिक एसिड (इलेक्ट्रोफाइल $SO_3$)।',
            },
            {
              english: '• **Friedel-Crafts alkylation**: $RX / AlCl_3$ → $C_6H_5R$. Limitations: rearrangement of cation; no $-NH_2$ substrate; no vinyl/aryl halides.',
        hindi: '• **फ़्रीडेल-क्राफ्ट्स एल्किलेशन**: $RX / AlCl_3$ → $C_6H_5R$। सीमाएँ: धनायन की पुनर्व्यवस्था; कोई $-NH_2$ सब्सट्रेट नहीं; कोई vinyl/aryl हैलाइड नहीं।',
            },
            {
              english: '• **Friedel-Crafts acylation**: $RCOCl / AlCl_3$ → ketone; no rearrangement.',
        hindi: '• **फ़्रीडेल-क्राफ्ट्स एसाइलेशन**: $RCOCl / AlCl_3$ → कीटोन; कोई पुनर्व्यवस्था नहीं. **निर्देशन प्रभाव:**',
            },
            {
              english: '**Directing effects:**',
        hindi: '**निर्देशन प्रभाव:**',
            },
            {
              english: '• o,p-directors (activating): $-OH, -OR, -NH_2, -NHR, -CH_3, -X$ (deactivating but o,p).',
        hindi: '• ओ,पी-निर्देशक (सक्रिय): $-OH, -OR, -NH_2, -NHR, -CH_3, -X$ (ओ,पी को छोड़कर निष्क्रिय)।',
            },
            {
              english: '• m-directors (deactivating): $-NO_2, -CN, -COOH, -COR, -SO_3H$.',
        hindi: '• एम-निर्देशक (निष्क्रिय): $-NO_2, -CN, -COOH, -COR, -SO_3H$।',
            },
          ],
        },
        {
          type: 'note',
          body: {
            english: 'Carcinogenic: benzene & polynuclear aromatics like benzo[a]pyrene (tobacco smoke).',
        hindi: 'कार्सिनोजेनिक: बेंजीन और बेंजो[ए]पाइरीन (तंबाकू का धुआं) जैसे पॉलीन्यूक्लियर एरोमैटिक्स।',
          },
        },
      ],
    },
  ],
  keyFormulas: [
    {
      latex: 'C_nH_{2n+2},\\ C_nH_{2n},\\ C_nH_{2n-2}',
      desc: {
        english: 'Alkane / alkene / alkyne',
        hindi: 'अल्केन / एल्केन / एल्काइन',
      },
    },
    {
      latex: '\\text{Markovnikov: H} \\to \\text{C with more H}',
      desc: {
        english: '',
        hindi: 'मार्कोवनिकोव: अधिक एच के साथ एच सी',
      },
    },
    {
      latex: '\\text{Anti-Markov (HBr + peroxide): radical}',
      desc: {
        english: '',
        hindi: 'एंटी-मार्कोव (एचबीआर + पेरोक्साइड): कट्टरपंथी',
      },
    },
    {
      latex: 'CaC_2 + 2H_2O \\to C_2H_2',
      desc: {
        english: 'Acetylene prep',
        hindi: 'एसिटिलीन तैयारी',
      },
    },
    {
      latex: '\\text{Lindlar} \\Rightarrow \\text{cis-alkene}',
      desc: {
        english: '',
        hindi: 'लिंडलर सिस-अल्कीन',
      },
    },
    {
      latex: 'Na/liq.NH_3 \\Rightarrow \\text{trans-alkene}',
      desc: {
        english: '',
        hindi: 'Na/liq.NH 3 ट्रांस-अल्कीन',
      },
    },
    {
      latex: '(4n+2)\\pi \\text{ rule for aromaticity}',
      desc: {
        english: '',
        hindi: '(4n+2) सुगन्धितता के लिए नियम',
      },
    },
  ],
  pyqs: [
    {
      year: 2024,
      exam: 'JEE Main',
      tag: 'Markovnikov',
      problem: {
        english: 'Major product of $CH_3-CH=CH_2 + HBr$ (no peroxide).',
        hindi: '$CH_3-CH=CH_2 + HBr$ का प्रमुख उत्पाद (कोई पेरोक्साइड नहीं)।',
      },
      answer: {
        english: '$CH_3-CHBr-CH_3$ (2-bromopropane)',
        hindi: '$CH_3-CHBr-CH_3$ (2-ब्रोमोप्रोपेन)',
      },
      solution: {
        english: 'H adds to terminal C; Br to more substituted C.',
        hindi: 'H टर्मिनल C से जुड़ता है; Br से अधिक प्रतिस्थापित C.',
      },
    },
    {
      year: 2023,
      exam: 'NEET',
      tag: 'Reagent',
      problem: {
        english: 'Reagent to convert alkyne to cis-alkene:',
        hindi: 'एल्काइन को सीआईएस-एल्कीन में परिवर्तित करने के लिए अभिकर्मक:',
      },
      answer: {
        english: 'Lindlar catalyst (Pd-CaCO₃ with Pb)',
        hindi: 'लिंडलर उत्प्रेरक (Pd-CaCO₃ Pb के साथ)',
      },
      solution: {
        english: 'Selective syn-addition of H₂.',
        hindi: 'H₂ का चयनात्मक सिन-जोड़।',
      },
    },
    {
      year: 2022,
      exam: 'JEE Main',
      tag: 'EAS',
      problem: {
        english: 'Product of nitration of toluene (mono):',
        hindi: 'टोल्यूनि (मोनो) के नाइट्रेशन का उत्पाद:',
      },
      answer: {
        english: 'o- and p-nitrotoluene (mostly)',
        hindi: 'ओ- और पी-नाइट्रोटोलुइन (ज्यादातर)',
      },
      solution: {
        english: '$-CH_3$ is o,p-directing & activating.',
        hindi: '$-CH_3$ ओ, पी-निर्देशन और सक्रिय कर रहा है।',
      },
    },
    {
      year: 2021,
      exam: 'NEET',
      tag: 'Test',
      problem: {
        english: 'Reagent to distinguish acetylene from ethylene:',
        hindi: 'एथिलीन से एसिटिलीन को अलग करने के लिए अभिकर्मक:',
      },
      answer: {
        english: 'Tollens / ammoniacal $AgNO_3$ — gives white ppt with terminal alkyne',
        hindi: 'टॉलेंस / अमोनियाकल $AgNO_3$ - टर्मिनल एल्काइन के साथ सफेद पीपीटी देता है',
      },
      solution: {
        english: 'Acidic H of $\\equiv CH$ replaced.',
        hindi: '$\\equiv CH$ का अम्लीय H प्रतिस्थापित किया गया।',
      },
    },
    {
      year: 2019,
      exam: 'JEE Main',
      tag: 'Ozonolysis',
      problem: {
        english: '$CH_3-CH=CH-CH_3 \\xrightarrow{O_3, Zn/H_2O}$ ?',
        hindi: '$CH_3-CH=CH-CH_3 \\xrightarrow{O_3, Zn/H_2O}$?',
      },
      answer: {
        english: '$2 CH_3CHO$ (acetaldehyde)',
        hindi: '$2 CH_3CHO$ (एसीटैल्डिहाइड)',
      },
      solution: {
        english: 'Reductive cleavage of double bond to two aldehydes.',
        hindi: 'दो एल्डीहाइडों के लिए दोहरे बंधन का रिडक्टिव क्लीवेज।',
      },
    },
  ],
  next: {
    slug: 'purification-organic-compounds',
    title: 'Purification & Characterisation of Organic Compounds',
  },
};

export default chapter;
